import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart } from "iconsax-react";
import Swal from "sweetalert2";

import MobileLayout from "../../components/MobileLayout";
import { useAuth } from "../../hooks/auth";
import { DataTypeVerbete, useSinalario } from "../../hooks/sinalario";
import { favoritesService } from "../../services/favoritesService";
import { VerbeteCard } from "../Sinalario/SinalarioList/styles";

import { PageBody, EmptyState, ListWrap } from "./styles";

const FavoritesPage: React.FC = () => {
  const { user } = useAuth();
  const { getVerbete } = useSinalario();
  const navigate = useNavigate();
  const [items, setItems] = useState<DataTypeVerbete[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user?.uid) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const favs = await favoritesService.getFavorites(user.uid);
      setItems(favs as DataTypeVerbete[]);
    } catch (e) {
      console.error(e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    load();
  }, [load]);

  const handleOpen = (v: DataTypeVerbete) => {
    navigate("/VerbeteDetails", { state: v });
  };

  const handleRemove = async (e: React.MouseEvent, v: DataTypeVerbete) => {
    e.stopPropagation();
    if (!user?.uid) return;
    const key = v.uid || v.palavra;
    const ok = await favoritesService.removeFavorite(user.uid, key);
    if (ok) {
      setItems((prev) => prev.filter((x) => (x.uid || x.palavra) !== key));
    } else {
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível remover o favorito.",
      });
    }
  };

  const handleRefreshVerbetes = useCallback(async () => {
    try {
      await getVerbete();
    } catch {
      /* ignore */
    }
  }, [getVerbete]);

  useEffect(() => {
    handleRefreshVerbetes();
  }, [handleRefreshVerbetes]);

  if (!user?.uid) {
    return (
      <MobileLayout headerTitle="Favoritos" headerType="private">
        <PageBody>
          <EmptyState>
            <Heart size={48} variant="Linear" color="#6E6A7C" />
            <h2>Entre na sua conta</h2>
            <p>Faça login para ver os sinais que você favoritou.</p>
          </EmptyState>
        </PageBody>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout headerTitle="Favoritos" headerType="private">
      <PageBody>
        {loading && (
          <p style={{ textAlign: "center", color: "#6E6A7C" }}>Carregando...</p>
        )}
        {!loading && items.length === 0 && (
          <EmptyState>
            <Heart size={48} variant="Linear" color="#6E6A7C" />
            <h2>Nenhum favorito ainda</h2>
            <p>
              Explore o sinalário e toque no coração para salvar palavras aqui.
            </p>
          </EmptyState>
        )}
        {!loading && items.length > 0 && (
          <ListWrap>
            {items.map((v) => (
              <VerbeteCard
                key={v.uid || v.palavra}
                onClick={() => handleOpen(v)}
              >
                <div className="card-main">
                  <div className="media-box">
                    {v.image ? (
                      <img src={v.image} alt={v.palavra} />
                    ) : (
                      <div className="letter-fallback">{v.letra}</div>
                    )}
                  </div>
                  <div className="card-body">
                    <div className="info">
                      <h3>{v.palavra}</h3>
                      <p>{v.descricao}</p>
                    </div>
                    <div className="favorite-action">
                      <Heart
                        size={22}
                        variant="Bold"
                        color="#EF4444"
                        onClick={(e) => handleRemove(e, v)}
                      />
                    </div>
                  </div>
                </div>
              </VerbeteCard>
            ))}
          </ListWrap>
        )}
      </PageBody>
    </MobileLayout>
  );
};

export default FavoritesPage;
